import {
  ChevronRight,
  Crown,
  LogOut,

  Palette,
  Settings,
  Sparkles,
  User,
  Users,
} from "lucide-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { seo } from "@/lib/seo";
import { useSession } from "@/hooks/use-session";
import { useAuth } from "@/hooks/use-auth";
import { StudentAvatar } from "@/lib/avatars";

export const Route = createFileRoute("/profile/")({
  head: () =>
    seo({
      title: "Your Profile — The Quantum Minds",
      description: "Your profile, appearance, settings and community.",
      noindex: true,
    }),
  component: ProfilePage,
});

const ROW =
  "flex w-full items-center justify-between border-b border-hairline px-5 py-5 text-left transition hover:bg-surface last:border-b-0";

function RowInner({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <span className="flex items-center gap-4">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-surface">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-1 block text-xs text-muted-foreground">{subtitle}</span>
      </span>
    </span>
  );
}

function ProfilePage() {
  const { profile } = useSession();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="mx-auto min-h-screen w-full max-w-2xl px-5 pb-32 pt-8 text-foreground">
      {/* PROFILE HEADER */}
      <div className="glass relative overflow-hidden rounded-[28px] p-6">
        <div className="aurora absolute inset-0 opacity-70" />
        <div className="relative flex items-center gap-4">
          <StudentAvatar
            id={profile?.avatarId}
            className="size-16 shadow-[var(--shadow-glow)]"
          />
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold">
              {profile?.fullName || "Your Name"}
            </h1>
            <p className="mt-1 truncate text-sm text-muted-foreground">
              {profile?.email || "Your account"}
            </p>
            {profile?.studyClass || profile?.targetExam ? (
              <p className="mt-2 truncate text-xs text-muted-foreground">
                {[profile?.studyClass, profile?.targetExam].filter(Boolean).join(" · ")}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* ACCOUNT */}
      <div className="mt-6">
        <h2 className="mb-3 px-1 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Account
        </h2>
        <div className="glass overflow-hidden rounded-[24px]">
          <Link to="/profile/edit" className={ROW}>
            <RowInner
              icon={<User size={20} className="text-primary" />}
              title="My Profile"
              subtitle="Avatar, class and target exam"
            />
            <ChevronRight size={18} className="text-muted-foreground" />
          </Link>




          <Link to="/appearance" className={ROW}>
            <RowInner
              icon={<Palette size={20} className="text-primary" />}
              title="Appearance"
              subtitle="Theme, dark mode, device appearance"
            />
            <ChevronRight size={18} className="text-muted-foreground" />
          </Link>

          <Link to="/profile/settings" className={ROW}>
            <RowInner
              icon={<Settings size={20} className="text-primary" />}
              title="Settings"
              subtitle="Plan details and account controls"
            />
            <ChevronRight size={18} className="text-muted-foreground" />
          </Link>
        </div>
      </div>

      {/* HELP & SUPPORT / COMMUNITY — reuses the existing community section */}
      <div className="mt-6">
        <h2 className="mb-3 px-1 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Help &amp; Support
        </h2>
        <Link
          to="/community"
          className="glass grid min-h-20 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 overflow-hidden rounded-[24px] px-5 py-4 transition hover:bg-surface active:scale-[0.99]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-surface text-primary">
            <Users size={20} />
          </span>
          <span className="min-w-0 text-left">
            <span className="block text-sm font-medium">Our Community</span>
            <span className="mt-1 block text-xs leading-5 text-muted-foreground">
              About The Quantum Minds &amp; the people behind it.
            </span>
          </span>
          <ChevronRight size={18} className="shrink-0 text-muted-foreground" />
        </Link>
      </div>

      {/* PREMIUM CTA */}
      <div className="glass relative mt-7 overflow-hidden rounded-[28px] border-primary/25 p-6">
        <div className="aurora absolute inset-0" />
        <div className="relative">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[image:var(--gradient-brand)] shadow-[var(--shadow-glow)]">
              <Crown size={21} className="text-primary-foreground" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
                Quantum Premium
              </p>
              <h3 className="mt-1 text-lg font-semibold">Take your preparation further.</h3>
            </div>
          </div>
          <div className="mb-5 flex items-start gap-3">
            <Sparkles size={17} className="mt-1 shrink-0 text-primary" />
            <p className="text-sm leading-6 text-muted-foreground">
              Serious preparation isn't about studying more. It's about studying smarter,
              with the right tools at the right time.
            </p>
          </div>
          <button
            onClick={() => navigate({ to: "/pricing" })}
            className="w-full rounded-2xl bg-[image:var(--gradient-brand)] px-5 py-4 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition duration-200 hover:scale-[1.01] active:scale-[0.99]"
          >
            Explore Premium Plans
            <span className="ml-2">→</span>
          </button>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Unlock the complete Quantum Mind experience
          </p>
        </div>
      </div>

      {/* SIGN OUT */}
      <button
        onClick={signOut}
        className="glass mt-7 flex w-full items-center gap-4 rounded-[24px] px-5 py-5 transition hover:bg-surface active:scale-[0.99]"
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-destructive/10">
          <LogOut size={20} className="text-destructive" />
        </span>
        <span className="text-sm font-medium">Sign out</span>
      </button>

      {/* BRAND QUOTE */}
      <div className="px-5 pb-4 pt-8 text-center">
        <p className="text-[15px] font-medium leading-7 text-muted-foreground">
          “You don't need more time.
          <br />
          You need a better way to use it.”
        </p>
        <div className="mx-auto mt-4 h-px w-12 bg-[image:var(--gradient-brand)]" />
        <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          The Quantum Minds
        </p>
      </div>
    </div>
  );
}
